import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryParams } from '../../../../../hooks/useQueryParams';
import { GoalId } from './types';
import { isEmpty, omitBy } from 'lodash';
import {
  CREATE_GOAL_ID,
  getGoalRoute,
  GOAL_ID_QUERY_PARAM,
  goalIdToStr,
  parseGoalId,
} from '../goalsLinks';
import { useGoalsContext } from './GoalsContext/GoalsContext';

export type SelectedGoalId = GoalId | null | typeof CREATE_GOAL_ID;

export const useSelectedGoal = (): {
  selectedGoalId: SelectedGoalId;
  setSelectedGoalId: (selectedGoalId: SelectedGoalId) => void;
  getGoalRouteById: (goalId: SelectedGoalId) => ReturnType<typeof getGoalRoute>;
} => {
  const history = useHistory();
  const queryParams = useQueryParams();
  const { isManagerView } = useGoalsContext();
  const selectedGoalId = deserializeGoalId(queryParams[GOAL_ID_QUERY_PARAM]);

  const getGoalRouteById = useCallback(
    (goalId: SelectedGoalId) => {
      const currentQueryParams = omitBy(
        {
          ...queryParams,
          [GOAL_ID_QUERY_PARAM]: serializeGoalId(goalId),
        },
        isEmpty,
      );
      const route = getGoalRoute(isManagerView);
      return route.withQueryParams(currentQueryParams);
    },
    [queryParams, isManagerView],
  );

  const setSelectedGoalId = useCallback(
    (newSelectedGoalId: SelectedGoalId) => {
      const newQueryParams = omitBy(
        {
          ...queryParams,
          [GOAL_ID_QUERY_PARAM]: serializeGoalId(newSelectedGoalId),
        },
        isEmpty,
      );
      const route = getGoalRoute(isManagerView);
      history.push(route.withQueryParams(newQueryParams).create({}));
    },
    [history, queryParams, isManagerView],
  );

  return { selectedGoalId, setSelectedGoalId, getGoalRouteById };
};

function deserializeGoalId(serialized: unknown): SelectedGoalId {
  if (typeof serialized !== 'string') {
    return null;
  }
  try {
    return parseGoalId(serialized);
  } catch (error) {
    return null;
  }
}

function serializeGoalId(goalId: SelectedGoalId): string | null {
  if (goalId === null) {
    return null;
  }
  return goalIdToStr(goalId);
}
